import React, { useContext, useEffect, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import { runtimeContextPayload } from './combatContextIdentity';
import { ensureSharingContext } from './sharingApi';
import { ingestStructuredResult, openSpecialistSource } from './provenanceApi';

export const SPECIALIST_SOURCES = [
  ['treasure-generator', 'Treasure Generator'],
  ['loot-contextualizer', 'Loot Contextualizer'],
  ['monster-harvest-salvage-generator', 'Monster Harvest & Salvage'],
  ['crafting-recipe-workshop-generator', 'Crafting, Recipe & Workshop'],
  ['shop-merchant-generator', 'Shop / Merchant'],
  ['magic-item-generator', 'Magic Item Generator'],
  ['universal-item-engine', 'Universal Item Engine'],
];

const STARTER = JSON.stringify({
  record: { structured: true },
  items: [{ source_item_id: 'item-1', name: 'Generated item', canonical_key: 'generated-item', item_type: 'OTHER', quantity: 1, data: {}, effect: {}, provenance: {} }],
  currency: {}, player_projection: { player_safe_facts: ['A structured result is ready.'] },
  dm_projection: { dm_only_facts: [] },
}, null, 2);

export default function GeneratorProvenanceWorkspace() {
  const { state } = useContext(CombatContext);
  const [identity, setIdentity] = useState(null);
  const [source, setSource] = useState('treasure-generator');
  const [productId, setProductId] = useState('');
  const [revision, setRevision] = useState('1');
  const [destination, setDestination] = useState('UNCLAIMED');
  const [visibility, setVisibility] = useState('DM_ONLY');
  const [structured, setStructured] = useState(STARTER);
  const [notice, setNotice] = useState('Connecting the canonical possession boundary…');

  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(state)).then(function (context) {
      if (!live) return;
      setIdentity(context);
      setNotice('Ready for an approved structured generator result.');
    }).catch(function () { if (live) setNotice('Persistent campaign context could not connect.'); });
    return function () { live = false; };
    // The adapter binds once to the active campaign/encounter identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openSource(slug) {
    try {
      const value = await openSpecialistSource(slug);
      setNotice('Opening the entitled specialist source in DCC…');
      if (typeof window !== 'undefined' && window.location && typeof window.location.assign === 'function') {
        window.location.assign(value.source_open_target);
      }
    } catch (_error) {
      setNotice('That specialist generator is not entitled or could not be opened.');
    }
  }

  async function importResult() {
    if (!identity || !productId.trim() || !revision.trim()) return;
    try {
      const parsed = JSON.parse(structured);
      const player = (identity.players || []).find(function (entry) { return String(entry.runtime_ref) === String(destination); });
      const destinationPayload = player ? { state: 'PERSONAL', player_id: player.player_id, character_id: player.character_id } : { state: destination };
      const recipients = player && visibility === 'SINGLE_PLAYER' ? [player.player_id] : [];
      const response = await ingestStructuredResult({
        campaign_id: identity.campaign_id, encounter_id: identity.encounter_id,
        source_generator_slug: source, source_product_id: productId.trim(),
        source_version: String(parsed.source_version || '1'), source_revision: revision.trim(),
        record: parsed.record || parsed, items: parsed.items || [], currency: parsed.currency || {},
        dm_projection: parsed.dm_projection || {}, player_projection: parsed.player_projection || {},
        visibility: visibility,
        recipient_player_ids: recipients, destination: destinationPayload,
        title: parsed.title || (SPECIALIST_SOURCES.find(function (row) { return row[0] === source; }) || [null, source])[1] + ' result',
      });
      setNotice((response.replayed ? 'Existing' : 'New') + ' canonical result accepted: ' + (response.items || []).length + ' item(s).');
    } catch (error) {
      setNotice(error instanceof SyntaxError ? 'Structured result is not valid JSON.' : 'The structured result was rejected; no possessions were changed.');
    }
  }

  return <section className="cp-generator-provenance" aria-label="Specialist generator possession boundary">
    <div className="cp-generator-provenance-head"><div><span className="cp-layer-eyebrow">Authority 07 source boundary</span><h3>Real generators → canonical possessions</h3></div><span className={identity ? 'vi-tr-status vi-tr-status-taken' : 'vi-tr-status vi-tr-status-unsearched'}>{identity ? 'CONNECTED' : 'CONNECTING'}</span></div>
    <p>Open the specialist source in DCC, generate there, then return its approved structured result. Combat PRO stores identity, visibility, ownership and useful mechanics; it does not recreate the generator.</p>
    <div className="cp-generator-source-grid">{SPECIALIST_SOURCES.map(function (row) { return <button type="button" className="vi-tr-btn" key={row[0]} onClick={function () { openSource(row[0]); }}>{row[1]}</button>; })}</div>
    <div className="cp-generator-import-grid">
      <label>Source<select aria-label="Structured result source" value={source} onChange={function (event) { setSource(event.target.value); }}>{SPECIALIST_SOURCES.map(function (row) { return <option key={row[0]} value={row[0]}>{row[1]}</option>; })}</select></label>
      <label>Source result ID<input aria-label="Source result ID" value={productId} onChange={function (event) { setProductId(event.target.value); }} placeholder="Stable result identity from source" /></label>
      <label>Revision<input aria-label="Source revision" value={revision} onChange={function (event) { setRevision(event.target.value); }} /></label>
      <label>Ownership<select aria-label="Ownership destination" value={destination} onChange={function (event) { setDestination(event.target.value); }}><option value="UNCLAIMED">Found / Unclaimed</option><option value="LEFT_BEHIND">Left behind</option><option value="PARTY">Party Inventory</option>{(identity && identity.players ? identity.players : []).map(function (player) { return <option key={player.player_id} value={player.runtime_ref}>Personal · {player.display_name}</option>; })}</select></label>
      <label>Visibility<select aria-label="Result visibility" value={visibility} onChange={function (event) { setVisibility(event.target.value); }}><option value="DM_ONLY">DM only</option><option value="PARTY">Party</option><option value="SINGLE_PLAYER">Selected owner only</option></select></label>
    </div>
    <label className="cp-generator-json">Approved structured result<textarea aria-label="Approved structured result" value={structured} onChange={function (event) { setStructured(event.target.value); }} /></label>
    <div className="vi-tr-actionbar"><button type="button" className="vi-tr-primary" disabled={!identity || !productId.trim()} onClick={importResult}>Assign canonical result</button><span role="status">{notice}</span></div>
  </section>;
}
